import { typographyTokens } from '../../../../theme/typographyTokens';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Stack,
  InputLabel,
  FormControlLabel,
  Checkbox,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Typography,
  Grid,
  useTheme
} from '@mui/material';
import { useAccount } from '@azure/msal-react';
import { TaskPhase, defaultSystemTask, standardTaskAssignee } from '../../../../../shared/types/Task';
import { useGetDepartmentsQuery } from '../../../../services/departmentSlice/departmentSlice';
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { Department } from '../../../../../shared/types/Department';
import { useSearchUsersQuery } from '../../../../services/usersSlice';
import { User } from '../../../../../shared/types/User';
import { debounce } from 'lodash';

const textFieldSx = {
  color: '#222222',
  '& > p': {
    margin: '5px 0px',
    color: '#646669',
    fontSize: typographyTokens.fontSizes.xs,
  },
};

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#646669',
    color: '#FBFCFC',
    boxShadow: theme.shadows[1],
    fontSize: typographyTokens.fontSizes.body,
  },
}));

const Placeholder = ({ children }: { children: string }) => {
  return <div style={{  fontSize: typographyTokens.fontSizes.body }}>{children}</div>;
};

const SecondStep = ({ task, handleChange, handleChangeOfDefaultAssignee, displayName }: any) => {
  const { data } = useGetDepartmentsQuery();
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [defaultAssigneeRadio, setDefaultAssigneeRadio]= useState<string>('');
  const account = useAccount();
  const roleList: any = useMemo(() => {
    return account?.idTokenClaims?.roles;
  }, [account]);
  const parsedRequestorSla = JSON.parse(task?.requestorSla);
  const parsedStakeholderSla = JSON.parse(task?.stakeholderSla);
  const { 'requestorSla2': requestorSla2, 'requestorSla3': requestorSla3 } = parsedRequestorSla || {};
  const { 'stakeholderSla1': stakeholderSla1, 'stakeholderSla2': stakeholderSla2, 'stakeholderSla3': stakeholderSla3 } = parsedStakeholderSla || {};

  // const departments: Department[] = [];

  const departments: Department[] = [...data || []];
  
  const { data: searchResults = [], isLoading, isError, error } = useSearchUsersQuery(
    {
      search: searchTerm,
      
    },
    { skip: searchTerm.trim() === '' }
  );
 
  
  const departmentList = data;

  if (roleList && roleList.length && data) {
    const superUser = roleList?.includes('AuthSuperUser');
    data.forEach((item: Department) => {
      const adRole = roleList?.find((role: any) => role === item.adAppRole);
      if (adRole || superUser) {
        departments.push(item);
      }
    });
  }

  const debouncedSearch = useRef<ReturnType<typeof debounce>>(debounce((term: string) => setSearchTerm(term), 100));
  useEffect(() => {
    debouncedSearch.current(searchTerm);
    return () => debouncedSearch.current.cancel();
  }, [searchTerm]);
  useEffect(() => {
    if (!isLoading && !isError) {
      setUserOptions((prevOptions) => {

        return JSON.stringify(prevOptions) !== JSON.stringify(searchResults) ? searchResults : prevOptions;
      });
    }
  }, [searchResults, isLoading, isError]);

  useEffect(()=>{
    setDefaultAssigneeRadio(task.defaultAssignee.length>12 ? 'Other': task.defaultAssignee)
    
  },[task])

  
  return (
    <Stack
      display="flex"
      direction="column"
      alignItems="left"
      spacing={4}
      sx={{ p: 4 }}
    >      
      <Box>
        <FormLabel
          id="demo-controlled-radio-buttons-group"
          sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : '#222222' }}
        >
          Default Assignee &nbsp;
          <LightTooltip title="The person the task will be assigned to by default. Tasks can reassigned from the task details screen.">
            <i className="fa-solid fa-circle-info"></i>
          </LightTooltip>
        </FormLabel>
        <RadioGroup
          aria-labelledby="demo-controlled-radio-buttons-group"
          name="defaultAssignee"
          value={defaultAssigneeRadio}
          onChange={(e) => {
              setDefaultAssigneeRadio(e.target.value)
              handleChange(e)
            }}
        >
          <FormControlLabel
            value="requestor"
            control={<Radio />}
            label="Requestor"
          />
          <FormControlLabel
            value="stakeholder"
            control={<Radio />}
            label="Stakeholder"
          />
          <FormControlLabel
            value="Other"
            control={<Radio />}
            label="Other"
          />
          <FormControlLabel
            value= {defaultSystemTask?.systemTask}
            control={<Radio />}
            label="System task"
          />
          <FormControlLabel
            value= {standardTaskAssignee?.assignByVariable}
            control={<Radio />}
            label={standardTaskAssignee?.assignByVariable}
          />
        </RadioGroup>
        {(defaultAssigneeRadio !== defaultSystemTask?.systemTask && defaultAssigneeRadio !== standardTaskAssignee?.assignByVariable) && (
          <FormControlLabel
            control={
              <Checkbox
                name="review"
                onChange={(e) => handleChange(e)}
                checked={task.review}
              />
            }
            label="This task requires a review"
            sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : "#222222", my: "20px" }}
          />
        )}
        {defaultAssigneeRadio === standardTaskAssignee?.assignByVariable && (
            <Box sx={{ width: { xs: "100%", lg: "100%" }, my: "20px" }}>
            <InputLabel
              sx={{
              color: theme.palette.mode === 'dark' ? theme.palette.white.main : "#222222",
              }}
            >
              Select Variable Id (Department ID of Stakeholder)
            </InputLabel>
            <Select
              sx={textFieldSx}
              size="small"
              fullWidth
              id="assigneeVariableId"
              name="assigneeVariableId"
              data-testid="task-variable-name"
              value={task?.assigneeVariableId || ""}
              onChange={(e) => handleChange(e)}
              displayEmpty
            >
              <MenuItem value="">
              <Placeholder>Select Variable Id</Placeholder>
              </MenuItem>
              {departmentList?.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                {/* {dept?.name} stakeholder */}
                {'{' + dept?.name + ' stakeholder}'}
              </MenuItem>
              ))}
            </Select>
            </Box>
        )}
       <Grid sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 5, mb: 2 }}>
          <Box sx={{ width: { xs: "100%", lg: "25%" } }}>
            <InputLabel
              sx={{
                color: theme.palette.mode === 'dark' ? theme.palette.white.main : "#222222",
              }}
            >
              Requestor SLA-1
            </InputLabel>
            <TextField
              sx={textFieldSx}
              size="small"
              id="taskAge"
              name="taskAge"
              style={{ width: 150 }}
              data-testid="task-id"
              value={task?.taskAge || ""}
              placeholder="SLA-1"
              helperText="Use for Management Escalation."
              onChange={(e) => handleChange(e)}
            />
          </Box>
          <Box sx={{ width: { xs: "100%", lg: "25%" } }}>
            <InputLabel
              sx={{
                color: theme.palette.mode === 'dark' ? theme.palette.white.main : "#222222",
              }}
            >
              Requestor SLA-2
            </InputLabel>
            <TextField
              sx={textFieldSx}
              size="small"
              id="requestorSla-2"
              name="requestorSla-2"
              style={{ width: 150 }}
              data-testid="task-id-req"
              value={requestorSla2 || ""}
              placeholder="SLA-2"
              helperText="Use for Management Escalation."
              onChange={(e) => {
                const updatedSla = {
                  ...parsedRequestorSla,
                  ["requestorSla2"]: e.target.value,
                };
                handleChange({
                  target: {
                    name: "requestorSla",
                    value: JSON.stringify(updatedSla),
                  },
                });
              }}
            />
          </Box>
          <Box sx={{ width: { xs: "100%", lg: "25%" } }}>
            <InputLabel
              sx={{
                color: theme.palette.mode === 'dark' ? theme.palette.white.main : "#222222",
              }}
            >
              Requestor SLA-3
            </InputLabel>
            <TextField
              sx={textFieldSx}
              size="small"
              id="requestorSla-3"
              name="requestorSla-3"
              style={{ width: 150 }}
              value={requestorSla3 || ""}
              placeholder="SLA-3"
              helperText="Use for Management Escalation."
              onChange={(e) => {
                const updatedSla = {
                  ...parsedRequestorSla,
                  ["requestorSla3"]: e.target.value,
                };
                handleChange({
                  target: {
                    name: "requestorSla",
                    value: JSON.stringify(updatedSla),
                  },
                });
              }}
            />
          </Box>
        </Grid>
        {task.review && defaultAssigneeRadio !== defaultSystemTask?.systemTask && defaultAssigneeRadio !== standardTaskAssignee?.assignByVariable && (
          <Grid sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 5, mb: 2 }}>
            <Box sx={{ width: { xs: "100%", lg: "25%" } }}>
              <InputLabel
                sx={{
                  color: theme.palette.mode === 'dark' ? theme.palette.white.main : "#222222",
                }}
              >
                Stakeholder SLA-1
              </InputLabel>
              <TextField
                sx={textFieldSx}
                size="small"
                id="StakeholderSla"
                name="StakeholderSla"
                style={{ width: 150 }}
                data-testid="task-id"
                value={stakeholderSla1 || ""}
                placeholder="SLA-1"
                helperText="Use for Management Escalation."
                onChange={(e) => {
                  const updatedSla = {
                    ...parsedStakeholderSla,
                    ["stakeholderSla1"]: e.target.value,
                  };
                  handleChange({
                    target: {
                      name: "stakeholderSla",
                      value: JSON.stringify(updatedSla),
                    },
                  });
                }}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "25%" } }}>
              <InputLabel
                sx={{
                  color: theme.palette.mode === 'dark' ? theme.palette.white.main : "#222222",
                }}
              >
                Stakeholder SLA-2
              </InputLabel>
              <TextField
                sx={textFieldSx}
                size="small"
                id="stakeholderSla"
                name="stakeholderSla"
                style={{ width: 150 }}
                data-testid="task-id"
                value={stakeholderSla2 || ""}
                placeholder="SLA-2"
                helperText="Use for Management Escalation."
                onChange={(e) => {
                  const updatedSla = {
                    ...parsedStakeholderSla,
                    ["stakeholderSla2"]: e.target.value,
                  };
                  handleChange({
                    target: {
                      name: "stakeholderSla",
                      value: JSON.stringify(updatedSla),
                    },
                  });
                }}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "25%" } }}>
              <InputLabel
                sx={{
                  color: theme.palette.mode === 'dark' ? theme.palette.white.main : "#222222",
                }}
              >
                Stakeholder SLA-3
              </InputLabel>
              <TextField
                sx={textFieldSx}
                size="small"
                id="stakeholderSla"
                name="stakeholderSla"
                style={{ width: 150 }}
                data-testid="task-id"
                value={stakeholderSla3 || ""}
                placeholder="SLA-3"
                helperText="Use for Management Escalation."
                onChange={(e) => {
                  const updatedSla = {
                    ...parsedStakeholderSla,
                    ["stakeholderSla3"]: e.target.value,
                  };
                  handleChange({
                    target: {
                      name: "stakeholderSla",
                      value: JSON.stringify(updatedSla),
                    },
                  });
                }}
              />
            </Box>
          </Grid>
        )}
        {defaultAssigneeRadio && defaultAssigneeRadio=== 'Other' && (
          <React.Fragment>
            <Autocomplete
              options={userOptions}
              getOptionLabel={(user) => user.displayName || user.email || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => {
                if(value?.id){
                 // setDefaultAssigneeValue(value.id) 
                  handleChangeOfDefaultAssignee(value.id)
                }
                if (value?.email) {
                  setErrorMessage('');
                } else {
                  setErrorMessage('Enter a valid user ID');
                }
              }}
              inputValue={searchTerm}
              onInputChange={(_, newInputValue) => debouncedSearch.current(newInputValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label= {displayName ? displayName :   "Search and Select Users"} 
                  sx={{
                    borderColor: task.defaultAssignee === 'Other' && searchTerm.trim() === '' ? 'red' : 'default',
                    '& input': {
                      fontSize: typographyTokens.fontSizes.body,
                      cursor: 'pointer',
                      color: task.defaultAssignee === 'Other' && searchTerm.trim() === '' ? 'red' : 'default',
                    },
                  }}
                />
              )}
              />
              {!displayName  && searchTerm.trim() === '' && (
                <Typography sx={{ color: 'red', fontSize: typographyTokens.fontSizes.caption, mt: 1 }}>
                  Enter a valid user ID
                </Typography>
              )}
            </React.Fragment>
          )}
      </Box>
      <Box>
      <InputLabel sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : '#222222' }}>Task Summary for Requestor or Other</InputLabel>
          <TextField
            sx={textFieldSx}
            size="small"
            id="requestorSummary"
            name="requestorSummary"
            fullWidth
            //data-testid="task-name"
            value={task.requestorSummary || ''}
            placeholder=""
            helperText="This should be the status summary when this task is locked with the requestor or other"
            onChange={(e) => handleChange(e)}
          />
        </Box>
        <Box>
      <InputLabel sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : '#222222' }}>Task Summary for Stakeholder</InputLabel>
          <TextField
            sx={textFieldSx}
            size="small"
            id="stakeholderSummary"
            name="stakeholderSummary"
            fullWidth
            //data-testid="task-name"
            value={task.stakeholderSummary || ''}
            placeholder=""
            helperText="This should be the status summary when this task is pending with the stakeholder"
            onChange={(e) => handleChange(e)}
          />
        </Box>
      <Box>
        
        <InputLabel sx={{ color: theme.palette.mode === 'dark' ? theme.palette.white.main : '#222222' }}>Department</InputLabel>
        <Select
          size="small"
          id="demo-simple-select-helper"
          value={task.departmentID}
          displayEmpty={true}
          name="departmentID"
          onChange={(e) => handleChange(e)}
          sx={{ width: '330px' }}
        >
          <MenuItem value={0}>
            <Placeholder>Choose a department from the dropdown</Placeholder>
          </MenuItem>
          {departments?.map((e, i) => (
            <MenuItem key={i} value={e.id}>
              {e.name}
            </MenuItem>
          ))}
        </Select>
        <Typography
          sx={{
            margin: '5px 0px',
            color: '#646669',
            fontSize: typographyTokens.fontSizes.xs,
          }}
        >
          Primary department that owns this template
        </Typography>
      </Box>

      {/* Multi-Department Task Section - NEW UI ONLY */}
      <Box sx={{ 
        p: 2, 
        border: '1px dashed #ccc', 
        borderRadius: '4px',
        // backgroundColor: '#f9f9f9' 
      }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(task.isMultiDepartment)}
              onChange={(e) => {
                const isChecked = e.target.checked;
                
                // Update both fields in a single handleChange call
                if (!isChecked) {
                  // Unchecking: set isMultiDepartment to false AND clear additionalDepartmentIDs array
                  handleChange({ 
                    target: { 
                      name: 'isMultiDepartment_and_additionalDepartmentIDs', 
                      value: { isMultiDepartment: false, additionalDepartmentIDs: [] } 
                    } 
                  });
                } else {
                  // Checking: just set isMultiDepartment to true
                  handleChange({ 
                    target: { 
                      name: 'isMultiDepartment', 
                      value: isChecked 
                    } 
                  });
                }
              }}
              name="isMultiDepartment"
              disabled={!task.departmentID || task.departmentID === 0}
            />
          }
          label={
            <Box>
              <span>Make Available to Multiple Departments</span>
              <LightTooltip title="Allow this template to be enabled by multiple departments. Each department can enable it once per SEP.">
                <i className="fa-solid fa-circle-info" style={{ marginLeft: '8px' }}></i>
              </LightTooltip>
            </Box>
          }
        />
        
        {task.isMultiDepartment && (
          <Box sx={{ mt: 2 }}>
            <InputLabel sx={{ mb: 1 }}>
              Additional Departments *
            </InputLabel>
            <Autocomplete
              multiple
              id="additional-department-select"
              size="small"
              options={departments?.filter(dept => dept.id !== task.departmentID) || []}
              getOptionLabel={(option) => option.name}
              value={
                (task.additionalDepartmentIDs || [])
                  .map((id: number) => departments?.find(d => d.id === id))
                  .filter(Boolean) as Department[]
              }
              onChange={(event, newValue) => {
                handleChange({
                  target: {
                    name: 'additionalDepartmentIDs',
                    value: newValue.map(dept => dept.id)
                  }
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={task.additionalDepartmentIDs?.length === 0 ? "Choose additional departments" : ""}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      padding: '4px',
                    }
                  }}
                />
              )}
              sx={{ width: '100%' }}
            />
            <Typography
              sx={{
                margin: '5px 0px',
                color: '#646669',
                fontSize: typographyTokens.fontSizes.xs,
              }}
            >
              Select one or more additional departments (primary: {departments?.find(d => d.id === task.departmentID)?.name || 'Not selected'})
            </Typography>
            {(!task.additionalDepartmentIDs || task.additionalDepartmentIDs.length === 0) && (
              <Typography
                sx={{
                  margin: '5px 0px',
                  color: '#d32f2f',
                  fontSize: typographyTokens.fontSizes.xs,
                }}
              >
                ⚠️ Please select at least one additional department for multi-department tasks
              </Typography>
            )}
          </Box>
        )}
      </Box>
      <Box>
        <InputLabel>Phase</InputLabel>
        <Select
          size="small"
          id="demo-simple-select-helper"
          name="phase"
          value={task.phase}
          displayEmpty={true}
          onChange={(e) => handleChange(e)}
          sx={{ width: '330px', textTransform: 'capitalize' }}
        >
          <MenuItem value="">
            <Placeholder >Choose a phase from the dropdown</Placeholder>
          </MenuItem>
          {Object.values(TaskPhase).map((e, i) => (
            <MenuItem key={i} value={e} sx={{ textTransform: 'capitalize' }}>
              {e}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Stack>
  );
};

export default SecondStep;
